

import React, { useState } from "react";
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft,
  Dashboard,
  Event,
  QrCode,
  People,
  ExitToApp,
  Assignment,
  Replay,
  Star,
  CameraAlt,
  ManageAccounts,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";

const drawerWidth = 240;
const miniDrawerWidth = 70;

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const isMobile = useMediaQuery("(max-width:900px)");

  // ✅ Get user from localStorage
  const userData = JSON.parse(localStorage.getItem("user"));

  if (!userData) {
    window.location.href = "/admin/login";
  }

  const { user } = userData?.data || {};
  const permissions = user?.permissions || [];

  const handleDrawerToggle = () => setOpen(!open);

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/admin/login");
  };

  // ✅ Define all possible menu items with permissions
  const menuItems = [
    { key: "dashboard", text: "Dashboard", icon: <Dashboard />, path: "/admin/dash" },
    { key: "events", text: "Events", icon: <Event />, path: "/settings" },
    { key: "normalUserList", text: "Gate Pass Records", icon: <Assignment />, path: "/userList" },
    { key: "redownload", text: "Re-Download Passes", icon: <Replay />, path: "/redownload" },

    { key: "vipEvents", text: "VIP Events", icon: <Star />, path: "/vipEvents" },

    { key: "vipUserList", text: "VIP Users", icon: <People />, path: "/vipUserList" },

    { key: "uniqueEvent", text: "Media Event", icon: <Event />, path: "/unique/settings" },
    { key: "uniqueUser", text: "Media Users", icon: <CameraAlt />, path: "/unique/userList" },
    { key: "contentManage", text: "Content Management", icon: <ManageAccounts />, path: "/contentManage" },
    { key: "referralCode", text: "Referral Codes", icon: <QrCode />, path: "/referral/code" },

    { key: "scanner", text: "Scanning List", icon: <QrCode />, path: "/scanner" },

    { key: "vipScanner", text: "VIP Scanner", icon: <QrCode />, path: "/vip/camera/scanner" },
    { key: "mediaScanner", text: "Media Scanner", icon: <QrCode />, path: "/media_scanner" },
    { key: "usersScanner", text: "User Scanner", icon: <QrCode />, path: "/user/scanner" },

    { key: "screenScanner", text: "BOL Scanner", icon: <QrCode />, path: "/vip/screen/scanner" },

    { key: "logout", text: "Logout", icon: <ExitToApp />, action: handleLogout },
  ];

  // ✅ Filter menu by permissions
  const allowedMenuItems = menuItems.filter((item) => {
    if (item.key === "logout") return true; // always show logout
    return permissions.includes(item.key);
  });

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* ======= TOP APP BAR ======= */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: {
            xs: "100%",
            sm: isMobile ? "100%" : `calc(100% - ${open ? drawerWidth : miniDrawerWidth}px)`
          },
          ml: {
            xs: 0,
            sm: isMobile ? 0 : `${open ? drawerWidth : miniDrawerWidth}px`
          },
          transition: "width 0.3s ease, margin-left 0.3s ease",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
          }}
        >
          {/* ======= MENU / COLLAPSE BUTTON ======= */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              backgroundColor: "#a31740",
              color: "white",
              "&:hover": { backgroundColor: "#a31740" },
            }}
          >
            {open && !isMobile ? <ChevronLeft /> : <MenuIcon />}
          </IconButton>

          {/* ======= TITLE ======= */}
          <Typography
            variant="h6"
            noWrap
            sx={{
              flexGrow: 1,
              textAlign: "center",
              color: "#a31740",
              fontWeight: 600,
            }}
          >
            Khasdar Mahotsav Admin
          </Typography>
        </Toolbar>
      </AppBar>

      {/* ======= DRAWER SIDEBAR ======= */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? open : true}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: open ? drawerWidth : miniDrawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : miniDrawerWidth,
            boxSizing: "border-box",
            backgroundColor: "#fff",
            transition: "width 0.3s ease",
            overflowX: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            pt: 2,
          }}
        >
          {/* LOGO SECTION */}
          <Box
            sx={{
              textAlign: "center",
              mb: 3,
              px: open ? 2 : 0,
            }}
          >
            <img
              src="/khasdar.jpg"
              alt="Logo"
              style={{
                width: open ? 120 : 50,
                height: open ? 120 : 50,
                objectFit: "contain",
                transition: "all 0.3s ease"
              }}
            />
          </Box>

          {/* MENU LIST */}
          <List sx={{ px: open ? 1 : 0 }}>
            {allowedMenuItems.map((item, index) => (
              <ListItem
                button
                key={index}
                component={item.action ? "button" : Link}
                to={item.path || "#"}
                onClick={item.action || null}
                sx={{
                  justifyContent: open ? "initial" : "center",
                  px: open ? 2 : 1,
                  "&:hover": {
                    backgroundColor: "#e0f2f1",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "#a31740",
                    minWidth: open ? 40 : "auto",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#333",
                    }}
                  />
                )}
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* ======= MAIN CONTENT ======= */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          mt: 8,
          width: {
            xs: "100%",
            sm: `calc(100% - ${open ? drawerWidth : miniDrawerWidth}px)`
          },
          ml: { xs: 0, sm: 0 },
          transition: "width 0.3s ease",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;